import {
    setIsCollectMode,
    fetchLpStateDataIds,
    heatmapClickClass,
    heatmapAttentionClass,
    heatmapArrivalClass,
    heatmapDataIdKey,
    heatmapHasLink,
    getHeatmapAttentionElements,
    isAttentionCandidateElement,
    getAttentionVisibleRect,
    getAttentionDataId,
    isAttentionBandVisible,
    hasHeatmapContentClassChanged,
    isVisibilityElement,
    setHeatmapAttribute,
    clickTargetTags,
    setClickHeatmapAttribute,
    getHeatmapDomChangeObserveElements,
    pickLpStateKeyValue,
    roundCalc,
    floorCalc,
    clamp01,
    decideThresholds,
    INTERSECTION_THRESHOLDS_MAP,
    DOM_STABLE_DURATION,
    DOM_INIT_FORCE_STANDBY_MS,
    DOM_CHANGE_DEBOUNCE_MS,
    HEATMAP_COLLECT_WS,
    HEATMAP_SOCKET_PARAM_KEY,
    getCookie,
    setCookie,
    getPageHeight,
    isFixed,
} from './heatmap-common.js';

// 100 ms ticks, one denominator per exposed visit/band. Bucket snapshots are
// cumulative and can be acknowledged/retried independently of additive clicks.
export function createAttentionClock() {
  const bucketMs = 30 * 60 * 1000;
  const bands = new Map();
  let active = new Map();
  const dirty = new Map();
  function flush(now) {
    for (const [key, since] of active) {
      const band = bands.get(key);
      let start = since;
      const end = Math.min(now, since + Math.max(0, 60000 - band.totalMs));
      while (start < end) {
        const bucketStart = Math.floor(start / bucketMs) * bucketMs;
        const until = Math.min(end, bucketStart + bucketMs);
        let record = band.records.get(bucketStart);
        if (!record) {
          record = { ms: 0 };
          band.records.set(bucketStart, record);
        }
        record.ms += until - start;
        band.totalMs += until - start;
        const ticks = Math.floor(record.ms / 100);
        if (ticks > 0 && ticks !== record.emittedTicks) {
          dirty.set(`${bucketStart}:${key}`, { key, bucketStart, firstSeenAt: band.firstSeenAt, ticks, count: band.firstBucket === bucketStart ? 1 : 0 });
          record.emittedTicks = ticks;
        }
        start = until;
      }
      active.set(key, now);
    }
  }
  return {
    update(keys, now) {
      flush(now);
      active = new Map([...keys].map(key => {
        if (!bands.has(key)) {
          const firstBucket = Math.floor(now / bucketMs) * bucketMs;
          bands.set(key, { firstSeenAt: now, firstBucket, totalMs: 0, records: new Map([[firstBucket, { ms: 0, emittedTicks: 0 }]]) });
          // A brief exposure still belongs in the denominator, even if it is
          // shorter than one 100 ms tick or ends just before a bucket boundary.
          dirty.set(`${firstBucket}:${key}`, { key, bucketStart: firstBucket, firstSeenAt: now, ticks: 0, count: 1 });
        }
        return [key, now];
      }));
    },
    flush,
    stop(now) { flush(now); active.clear(); },
    snapshots() { const records = [...dirty.values()]; dirty.clear(); return records; },
  };
}

export const heatmapCollect = async() => {
    setIsCollectMode(true);

    const IS_TOUCH_DEVICE = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia('(pointer:coarse)').matches;
    if(!IS_TOUCH_DEVICE) {
      return;
    }

    const isLocal = location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname === '::1';
    if (isLocal) {
      return;
    }

    const currentUrl = new URL(location.href);
    const currentPathname = currentUrl.pathname.replace(/\/index\.(?:html?|php)$/i, '/');
    const domain = currentUrl.hostname;

    let windowWidth = window.innerWidth;
    let windowHeight = window.innerHeight;

    window.addEventListener('resize', () => {
      windowWidth = window.innerWidth;
      windowHeight = window.innerHeight;
    }, { passive: true });

    const uuidKey = 'c-heatmap-uuid';
    const uuid = (() => {
      const genUuid  = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

      try {
        const ck = localStorage.getItem(uuidKey);
        const u = ck ?? genUuid ();
        if(!ck) {
          localStorage.setItem(uuidKey, u);
        }
        return u;
      } catch (e) {
        const ck = getCookie(uuidKey);
        const u = ck ?? genUuid ();
        if(!ck) {
          setCookie(uuidKey, u, {maxAge: 30 * 24 * 60 * 60, sameSite: "Lax", path: "/",});
        }
        return u;
      }
    })();

    const agentId = getCookie('agent_id');
    const adId = getCookie('ad_id');
    const placement = getCookie('placement');
    const adMedia = getCookie('ad_media');

    const normalizeAdNetwork = (value) => {
      if (typeof value !== 'string') return 'unknown';

      const normalized = value.trim().toLowerCase();
      if (
        normalized.length === 0
        || normalized.length > 255
        || /[\u0000-\u001f\u007f]/.test(normalized)
      ) {
        return 'unknown';
      }

      return normalized;
    };

    const adNetwork = normalizeAdNetwork(placement || adMedia);
    
    if(uuid) {
      setCookie(HEATMAP_SOCKET_PARAM_KEY, JSON.stringify({
        domain: domain,
        uri: currentPathname,
        uuid: uuid,
        payload: {
          agentId: agentId,
          adId: adId,
          adNetwork: adNetwork,
        }
      }), {maxAge: 30 * 24 * 60 * 60, sameSite: "Lax", path: "/",});
    }

    // PV
    let isPvSents = {};
    let pvResendQueue = {};

    // ヒートマップ用属性付与
    setHeatmapAttribute(document);

    let heatmapAttentionElements = await getHeatmapAttentionElements();
    let heatmapDomChangeObserveElements = await getHeatmapDomChangeObserveElements();

    let lpStateDataIds = await fetchLpStateDataIds(domain, currentPathname);
    let lpStateKey = pickLpStateKeyValue(lpStateDataIds);

    if (lpStateKey) sendPvData(lpStateKey);

    // 離脱キャッシュ
    let isCtaClickLeaving = false;
    let leaveElelementPositionCache = {};

    // 概算Byte量
    let approxBytes = 0;
    const APPROX_MAX = 28 * 1024; // 28kb
    // 送信許容バイト数
    const BEACON_SIZE_MAX = 32 * 1024; // 32KB

    // スクロールフラグ
    let hasScrolled = false;
    let scrollActionCount = 0;
    let scrollCountDebounceTimer = null;
    const SCROLL_STOP_THRESHOLD_MS = 1000;
    const SCROLL_THRESHOLD_PX = 100;
    const LEAVE_NO_SCROLL = 'leave-no-scroll'; // 一度もスクロールしなかった
    const LEAVE_FIRST_SCROLL = 'leave-first-scroll'; // 一回だけスクロールして離脱

    // クリックデータ格納用
    let clickData = {};
    const CLICK_DATA_MAX = 200;

    // 滞在データ格納用
    let attentionData = {};

    // 到達率データ格納用
    let arrivalData = {};
    let arrivalLastKeyMap = {};
    let arrivalLastRatioMap = {};
    const ARRIVAL_DATA_MAX = 400;

    // 早期離脱を拾うためキャッシュを早期に作成
    updateLeaveElementPositionCache();

    // window系イベントは早期に登録
    setWindowEventListener();


    //=================================
    // スクロール系フラグの回収
    //=================================
    const onScrollHandler = () => {
      if (window.scrollY < SCROLL_THRESHOLD_PX) return;

      if (!hasScrolled) hasScrolled = true;

      // スクロールが止まって所定時間止まってたら1スクロールとしてカウント
      if (scrollCountDebounceTimer) {
        clearTimeout(scrollCountDebounceTimer);
      }

      scrollCountDebounceTimer = setTimeout(() => {
        scrollActionCount++;
        
        // 離脱データも作っておく
        updateLeaveElementPositionCache();
        
        scrollCountDebounceTimer = null; 
      }, SCROLL_STOP_THRESHOLD_MS);
    };

    window.addEventListener('scroll', onScrollHandler, {passive: true});

    //=================================
    // クリックヒートマップ ↓↓↓
    //=================================
    let clickBufferHeatmapId = '';
    let clickBufferX = 0;
    let clickBufferY = 0;
    let clickBufferLeft = 0;
    let clickBufferTop = 0;
    let clickBufferWidth = 0;
    let clickBufferHeight = 0;
    let clickBufferTime = 0;
    let lastClickTime = 0;
    let clickBufferLpStateKey = null;
    let clickBufferEl = null;
    let clickBufferPointerId = null;
    let clickBufferStepX = 0.01;
    let clickBufferStepY = 0.01;

    const clickStep = (px) => {
      if (px <= 120) return 0.04; // 超小さい
      if (px <= 240) return 0.02; // 小さい
      if (px <= 480) return 0.01; // 通常
      if (px <= 960) return 0.005; // 大きめ
      return 0.01;
    };

    const isClickElTop = (relativeX, relativeY) => {
      const dt = performance.now() - clickBufferTime;
      if (dt < 50 || dt > 300) {
        return false;
      }

      if (!Number.isFinite(clickBufferWidth) || !Number.isFinite(clickBufferHeight) || clickBufferWidth <= 0 || clickBufferHeight <= 0) {
        return false;
      }

      const dx = Math.round(clickBufferX * clickBufferWidth);
      const dy = Math.round(clickBufferY * clickBufferHeight);
      const cx = Math.round(relativeX * clickBufferWidth);
      const cy = Math.round(relativeY * clickBufferHeight);
      const moveOkPx = 5;

      if(Math.abs(dx - cx) > moveOkPx) {
        return false;
      }

      if(Math.abs(dy - cy) > moveOkPx) {
        return false;
      }

      return true;
    };

    document.addEventListener('pointerdown', (e) => {
      const el = e.target.closest(clickTargetTags.join(','));
      if (!el) return;

      if(setClickHeatmapAttribute) {
        setClickHeatmapAttribute(el);
      }

      // 小さすぎるのは拾わない
      const rect = el.getBoundingClientRect();
      if (rect.width < 12 || rect.height < 12) return;

      clickBufferEl = el;
      clickBufferPointerId = e.pointerId;
      clickBufferHeatmapId = el.getAttribute(heatmapDataIdKey);
      clickBufferStepX = clickStep(rect.width);
      clickBufferStepY = clickStep(rect.height);
      clickBufferX = roundCalc((e.clientX - rect.left) / rect.width, clickBufferStepX);
      clickBufferY = roundCalc((e.clientY - rect.top) / rect.height, clickBufferStepY);
      clickBufferLeft = rect.left;
      clickBufferTop = rect.top;
      clickBufferWidth = rect.width;
      clickBufferHeight = rect.height;
      clickBufferTime = performance.now();
      clickBufferLpStateKey = lpStateKey;

    }, {passive: true});

    document.addEventListener('pointerup', (e) => {
      if (clickBufferPointerId !== e.pointerId) {
        clickBufferLpStateKey = null;
        return;
      }
      
      const el = clickBufferEl;
      clickBufferEl = null;
      clickBufferPointerId = null;

      if (!el) {
        clickBufferLpStateKey = null;
        return;
      }

      const heatmapId = el.getAttribute(heatmapDataIdKey);

      if(heatmapId !== clickBufferHeatmapId) {
        clickBufferLpStateKey = null;
        return;
      }

      if(clickBufferLpStateKey === null) {
        clickBufferLpStateKey = null;
        return;
      }

      const clickNow = performance.now();
      if ((clickNow - lastClickTime) < 200) {
        clickBufferLpStateKey = null;
        return;
      }
      
      const relativeX = roundCalc((e.clientX - clickBufferLeft) / clickBufferWidth, clickBufferStepX).toFixed(4).replace(/\.?0+$/, '');
      const relativeY = roundCalc((e.clientY - clickBufferTop) / clickBufferHeight, clickBufferStepY).toFixed(4).replace(/\.?0+$/, '');

      if(!isClickElTop(relativeX, relativeY)) {
        clickBufferLpStateKey = null;
        return;
      }

      const isLink = Number(el.getAttribute(heatmapHasLink));

      if(isLink == 1) {
          // クリックによる遷移は離脱イベントとして判定しない            
          isCtaClickLeaving = true;
          // CVフラグ
          const postData = {};
          postData['uuid'] = uuid;
          postData['domain'] = domain;
          postData['uri'] = currentPathname;
          postData['payload'] = {
            agentId: agentId,
            adId: adId,
            adNetwork: adNetwork,
            cta: true,
          };

          const ajaxUrl = HEATMAP_COLLECT_WS;
          const payload = JSON.stringify(postData);
          const ok = navigator.sendBeacon(ajaxUrl, payload);

          if(!ok) {
            fetch(ajaxUrl, {method: 'POST', body: payload, keepalive: true}).catch(() => {});
          }
      }

      lastClickTime = clickNow;  
      const key = `s:${clickBufferLpStateKey},id:${heatmapId},x:${relativeX},y:${relativeY},a:${isLink}`;
      
      if(clickData[key] == undefined) {
          clickData[key] = 0;
          approxBytes += key.length + 4;
      }

      clickData[key]++;

      if(approxBytes >= APPROX_MAX || Object.keys(clickData).length >= CLICK_DATA_MAX) {   
          sendEventData('clickDataMax');
      }

      clickBufferLpStateKey = null;
    }, {
      passive: true // 既定でスクロールを阻害しない
    });

    document.addEventListener('pointercancel', (e) => {
      if (clickBufferPointerId !== e.pointerId) return;
      clickBufferEl = null;
      clickBufferPointerId = null;
      clickBufferLpStateKey = null;
    }, { passive: true });

    //=================================
    // クリックヒートマップ ↑↑↑
    //=================================

    //=================================
    // 滞在ヒートマップ＆読了率 ↓↓↓
    //=================================

    const attentionObserver = new IntersectionObserver(() => syncAttentionTimer(), { threshold: [0, 0.01, 1] });
    const attentionObserverActions = { observe: el => attentionObserver.observe(el), disconnect: () => attentionObserver.disconnect() };
    let visitId = makeVisitId();
    let attentionClock = createAttentionClock();
    let serverEpoch = null;
    let clockProbeRunning = false;
    let foreground = document.visibilityState !== 'hidden';
    let hiddenAt = null;
    const attentionQueue = new Map();
    let attentionSending = false;
    const attentionNow = () => serverEpoch === null ? null : serverEpoch + performance.now();
    function makeVisitId() {
      return crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
    async function calibrateAttentionClock() {
      if (clockProbeRunning || serverEpoch !== null || !foreground) return;
      clockProbeRunning = true;
      const started = performance.now();
      try {
        // Safe to retry: this request carries no additive metrics.
        const response = await fetch(HEATMAP_COLLECT_WS, { method: 'POST', body: JSON.stringify({
          domain, uri: currentPathname, uuid, payload: { agentId, adId, adNetwork, attentionVersion: 2, visitId, attentionData: {} }
        }) });
        if (!response.ok) throw new Error('attention clock');
        const { serverTime } = await response.json();
        if (!Number.isFinite(serverTime)) throw new Error('attention clock response');
        serverEpoch = serverTime - (started + performance.now()) / 2;
        syncAttentionTimer();
      } catch (_) { /* retry on heartbeat */ }
      finally { clockProbeRunning = false; }
    }
    function syncAttentionTimer() {
      const now = attentionNow();
      if (now === null) return;
      const keys = new Set();
      if (lpStateKey && foreground && document.visibilityState !== 'hidden') {
        for (const el of heatmapAttentionElements) {
          if (!el.classList.contains(heatmapAttentionClass)) continue;
          const visible = getAttentionVisibleRect(el);
          if (!visible) continue;
          const id = getAttentionDataId(el);
          const structuralId = el.getAttribute(heatmapDataIdKey);
          if (!id || !structuralId) continue;
          const rect = el.getBoundingClientRect();
          const step = decideThresholds(rect.height);
          for (const r of INTERSECTION_THRESHOLDS_MAP[step] || []) {
            if (Math.round(r * 10000) % Math.round(step * 10000) !== 0) continue;
            // Count a band only while its centre is visible.
            const centre = rect.top + Math.min(1 - step / 2, r + step / 2) * rect.height;
            if (visible.top <= centre && centre < visible.bottom && isAttentionBandVisible(el, visible, centre)) keys.add(`s:${lpStateKey},id:${id},r:${r}`);
          }
          collectArrival(el, structuralId, clamp01((visible.bottom - rect.top) / rect.height), rect.height);
        }
      }
      attentionClock.update(keys, now);
      if (approxBytes >= APPROX_MAX || Object.keys(arrivalData).length > ARRIVAL_DATA_MAX) sendEventData('arrivalMax');
    }
    function collectArrival(el, heatmapId, effectiveRatio, rectHeight) {
        // ====================
        // 到達率
        // ====================
        //要素サイズからarrivalStepを取得
        const arrivalStep = (() => {
          if (rectHeight < 120) return 0;
          if (rectHeight <= 360) return 0.25;
          if (rectHeight <= 1080) return 0.125;
          return 0.0625;
        })();

        if(0 < arrivalStep && 0 < Number(effectiveRatio) && el.classList.contains(heatmapArrivalClass)) {
          const arrivalRatio = roundCalc(clamp01(effectiveRatio), arrivalStep);
          const arrivalDataSetKey = `s:${lpStateKey},id:${heatmapId}`;

          // 安全に比較するため未定義は-1
          let prevRatio = Number(arrivalLastRatioMap[arrivalDataSetKey] ?? -1);

          if (arrivalRatio > prevRatio) {
              for (let r = arrivalStep; r <= arrivalRatio; r += arrivalStep) {
                  if (r > prevRatio) {
                      const ratio = roundCalc(r, arrivalStep).toFixed(4).replace(/\.?0+$/, '');
                      const arrivalDataKey = `${arrivalDataSetKey},r:${ratio}`;
                      
                      // データ量加算
                      if (arrivalData[arrivalDataKey] === undefined) {
                          approxBytes += arrivalDataKey.length + 4;
                      }
                      arrivalData[arrivalDataKey] = 1;
                  }
              }
              // 最大値を更新
              arrivalLastRatioMap[arrivalDataSetKey] = arrivalRatio;
          }
        }

    }
    function finalizeAttentionCollect() {
      const now = attentionNow();
      if (now !== null) attentionClock.stop(now);
      enqueueAttention();
    }
    function flushAttentionCollect() {
      const now = attentionNow();
      if (now !== null) attentionClock.flush(now);
      enqueueAttention();
    }
    function enqueueAttention() {
      for (const record of attentionClock.snapshots()) {
        const key = `${visitId}:${record.bucketStart}:${record.key}`;
        attentionQueue.set(key, { ...record, visitId, queueKey: key });
      }
    }
    function attentionPackets() {
      const packets = [];
      let packet = null;
      const now = attentionNow();
      const encoder = new TextEncoder();
      const groups = new Map();
      for (const [queueKey, row] of attentionQueue) {
        if (now !== null && now >= row.bucketStart + 60 * 60 * 1000) { attentionQueue.delete(queueKey); continue; }
        const groupKey = `${row.visitId}:${row.bucketStart}`;
        if (!groups.has(groupKey)) groups.set(groupKey, []);
        groups.get(groupKey).push(row);
      }
      for (const rows of groups.values()) for (const row of rows) {
        if (!packet || packet.visitId !== row.visitId || packet.bucketStart !== row.bucketStart || packet.bytes > 24000 || packet.rows.length >= 300) {
          packet = { visitId: row.visitId, bucketStart: row.bucketStart, rows: [], data: {}, bytes: 600 };
          packets.push(packet);
        }
        const value = { ticks: row.ticks, count: row.count, firstSeenAt: row.firstSeenAt };
        packet.rows.push(row);
        packet.data[row.key] = value;
        packet.bytes += encoder.encode(JSON.stringify({ [row.key]: value })).byteLength;
      }
      return packets.map(packet => ({ ...packet, body: JSON.stringify({ domain, uri: currentPathname, uuid, payload: {
        agentId, adId, adNetwork, attentionVersion: 2, visitId: packet.visitId, bucketStart: packet.bucketStart, attentionData: packet.data
      } }) }));
    }
    async function sendAttention(background = false) {
      if (background) {
        for (const packet of attentionPackets()) {
          try { navigator.sendBeacon(HEATMAP_COLLECT_WS, packet.body); } catch (_) {}
        }
        // Beacon acceptance isn't a server acknowledgement. Retain snapshots.
        return;
      }
      if (attentionSending || !foreground) return;
      attentionSending = true;
      try {
        for (const packet of attentionPackets()) {
          const response = await fetch(HEATMAP_COLLECT_WS, { method: 'POST', body: packet.body, keepalive: true });
          if (!response.ok) break;
          for (const row of packet.rows) if (attentionQueue.get(row.queueKey) === row) attentionQueue.delete(row.queueKey);
        }
      } catch (_) { /* snapshots remain queued */ }
      finally { attentionSending = false; }
    }
    function pauseAttention() {
      foreground = false;
      hiddenAt ??= performance.now();
      finalizeAttentionCollect();
      sendAttention(true);
    }
    function resumeAttention(newVisit = false) {
      if (document.visibilityState === 'hidden') return;
      if (newVisit || (hiddenAt !== null && performance.now() - hiddenAt >= 30 * 60 * 1000)) {
        visitId = makeVisitId();
        attentionClock = createAttentionClock();
        isPvSents = {};
        if (lpStateKey) sendPvData(lpStateKey);
      }
      hiddenAt = null;
      foreground = document.visibilityState !== 'hidden';
      calibrateAttentionClock();
      syncAttentionTimer();
      sendAttention();
    }
    let attentionFrame = null;
    const scheduleAttention = () => {
      if (attentionFrame !== null) return;
      attentionFrame = requestAnimationFrame(() => { attentionFrame = null; syncAttentionTimer(); });
    };
    // Tall media can scroll while its intersection ratio remains unchanged.
    document.addEventListener('scroll', scheduleAttention, { passive: true, capture: true });
    window.addEventListener('resize', scheduleAttention, { passive: true });
    const attentionResizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleAttention) : null;
    function observeAttentionElements() {
      attentionObserverActions.disconnect();
      attentionResizeObserver?.disconnect();
      attentionResizeObserver?.observe(document.body);
      for (const el of heatmapAttentionElements) {
        attentionObserverActions.observe(el);
        attentionResizeObserver?.observe(el);
      }
    }
    observeAttentionElements();
    calibrateAttentionClock();
    setInterval(() => {
      if (!foreground) return;
      calibrateAttentionClock();
      syncAttentionTimer();
      flushAttentionCollect();
      sendAttention();
    }, 15000);

    //=======================
    // DOMの変更を監視 ↓↓↓
    // ※ライブラリなどによる初期化でDOMが安定しない場合があるので、DOMが安定してから処理開始
    //=======================
    let attachDomChangeObserverTimerId = null;

    const attachDomChangeObserver = async() => {
        //一応ポリフィル化
        const runIdle = window.requestIdleCallback || ((fn, _) => setTimeout(fn, 100));
        const cancelIdle = window.cancelIdleCallback || clearTimeout;

        if (attachDomChangeObserverTimerId) {
          cancelIdle(attachDomChangeObserverTimerId);
        }

        attachDomChangeObserverTimerId = runIdle(async() => {
          domChangeObserver.disconnect();
          setHeatmapAttribute(document);
          heatmapAttentionElements = await getHeatmapAttentionElements();
          heatmapDomChangeObserveElements = await getHeatmapDomChangeObserveElements();

          const oldLpStateKey = lpStateKey;
          const newLpStateKey = pickLpStateKeyValue(lpStateDataIds);

          if(oldLpStateKey !== newLpStateKey) {
            finalizeAttentionCollect(lpStateKey);  

            if(approxBytes >= APPROX_MAX) {
                sendEventData('stateChangeEvent');
            }

            lpStateKey = newLpStateKey;
            if (lpStateKey) sendPvData();
          } else {
            lpStateKey = newLpStateKey;
          }

          heatmapDomChangeObserveElements.forEach((el) => {
              domChangeObserver.observe(el, {
                  attributes: true,
                  attributeFilter:['style','class','src','srcset','poster','hidden','data-c-heatmap-content-key'],
                  attributeOldValue: true,
              });
          });

          observeAttentionElements();
          domChangeObserver.observe(document.body, {childList: true, characterData: true, subtree: true, attributes: true, attributeOldValue: true, attributeFilter: ['style', 'class', 'src', 'srcset', 'poster', 'hidden', 'data-c-heatmap-content-key']});

          if (lpStateKey) {
              syncAttentionTimer();
              // 離脱用キャッシュ更新
              updateLeaveElementPositionCache();
          }

        }, {timeout: 1000});
    }

    let domChangedTimerId = null;
    const domChangeObserver = new MutationObserver((mutations) => {
        let isChanged = false;
        for (const m of mutations) {
          if (m.type === 'childList' || m.type === 'characterData') { isChanged = true; break; }
          if (m.type !== 'attributes') continue;
          if (m.attributeName === 'class' && !hasHeatmapContentClassChanged(m.oldValue, m.target.getAttribute('class'))) continue;
          isChanged = true;
          break;
        }
        if (isChanged) syncAttentionTimer();

        if(isChanged) {
            mutations.forEach((_, i) => {
                //普通にデバウンスしても駄目なので先に全部のmutation捌いてからデバウンスに入る
                if(i === mutations.length - 1) {
                    clearTimeout(domChangedTimerId);
                    domChangedTimerId = setTimeout(async() => {
                        attachDomChangeObserver();
                    }, DOM_CHANGE_DEBOUNCE_MS);
                }
            });
        }
    });

    {
        //※初期DOMが安定してから処理開始
        let initCompleteTimer = null;
        let domWatchStartTime = Date.now();
        let lastBodyHeight = getPageHeight();
        let lastHeightChangedAt = domWatchStartTime;
  
        const initCompleteObserver = new MutationObserver(() => {
            const h = getPageHeight();
  
            if(h != lastBodyHeight) {
                lastBodyHeight = h;
                lastHeightChangedAt = Date.now();
            }
  
            clearTimeout(initCompleteTimer);
            initCompleteTimer = setTimeout(async() => {
                const now = Date.now();
                const stableElapsed = now - lastHeightChangedAt;
                const totalElapsed  = now - domWatchStartTime;
  
                if(stableElapsed >= DOM_STABLE_DURATION || totalElapsed >= DOM_INIT_FORCE_STANDBY_MS) {
                    initCompleteObserver.disconnect();
                    clearTimeout(initCompleteTimer);
                    initCompleteTimer = null;
  
                    // ↓ DOMの監視を開始 ↓
                    await attachDomChangeObserver();
                    // ↑ DOMの監視を開始 ↑
                }
            }, DOM_STABLE_DURATION);
        });
  
        heatmapDomChangeObserveElements.forEach((el) => {
            initCompleteObserver.observe(el, {
                attributes: true,
            });
        });
  
        //所定時間経ってもMutationObserverによる検知がない場合、安定とみなす。
        const forceInitTimer = setTimeout(async() => {
            clearTimeout(forceInitTimer);
            if(initCompleteTimer === null && initCompleteObserver.takeRecords().length === 0) {
                initCompleteObserver.disconnect();
  
                // ↓ DOMの監視を開始 ↓
                await attachDomChangeObserver();
                // ↑ DOMの監視を開始 ↑
             }
        }, DOM_INIT_FORCE_STANDBY_MS);
    }

    const io = new IntersectionObserver(async(entries) => {
      if (entries.some(en => en.isIntersecting)) {
        await attachDomChangeObserver();
      }
    }, { threshold: 0 });
    
    for(const el of heatmapDomChangeObserveElements) {
      io.observe(el);
    }
    //=======================
    // DOMの変更を監視 ↑↑↑
    //=======================

    /**
     * イベント登録
     */
    function setWindowEventListener () {
        document.addEventListener('visibilitychange', function(e) {
            if (document.visibilityState === "hidden") {
                pauseAttention();
                sendEventData('visibilitychangehidden', {leaveAttach: true});
            } else if (document.visibilityState === "visible") {
                isCtaClickLeaving = false;
                resumeAttention();
            }
        }, false);

        window.addEventListener('pageshow', function(e) {
            isCtaClickLeaving = false; 

            if (e.persisted) {
                resumeAttention(true);
            }
        });

        window.addEventListener('pagehide', function(e) {
            pauseAttention();
            sendEventData('pagehide', {leaveAttach: true});
        });

        window.addEventListener('blur', function(e) {
            pauseAttention();
            sendEventData('blur');
        });

        window.addEventListener('focus', () => resumeAttention());
        document.addEventListener('click', (e) => { 
            if(e.target.closest('a')) {
                sendEventData('click');
            }
        });

        let loadResourceTimerId = null;
        const resourceChanged = (e) => {
          syncAttentionTimer();
          const t = e.target;
          if (t && (t.tagName === 'IMG' || t.tagName === 'VIDEO')) {
              clearTimeout(loadResourceTimerId);
              loadResourceTimerId = setTimeout(async () => {
                  requestAnimationFrame(async () => {
                      attachDomChangeObserver();
                  });
              }, 100);
          }
        };
        for (const event of ['load', 'loadeddata', 'loadedmetadata', 'emptied', 'error']) document.addEventListener(event, resourceChanged, true);
    };

    /**
     * PVデータ送信関数
     * @param {*} argLpStateKey 
     * @returns 
     */
    function sendPvData (argLpStateKey = lpStateKey) {
      const sendLpStateKey = argLpStateKey;
      if (!sendLpStateKey) return;
      if (isPvSents[sendLpStateKey]) return;

      isPvSents[sendLpStateKey] = true;

      const ajaxUrl = HEATMAP_COLLECT_WS;
      const postData = {};
      postData['uuid'] = uuid;
      postData['domain'] = domain;
      postData['uri'] = currentPathname;
      postData['payload'] = {
        agentId: agentId,
        adId: adId,
        adNetwork: adNetwork,
        pvData: {
          [sendLpStateKey]: true,
        }
      }

      const payload = JSON.stringify(postData);
      let ok = false;
      try {
        ok = navigator.sendBeacon(ajaxUrl, payload);
      } catch (e) {
        ok = false;
      }

      if(ok) {
        isPvSents[sendLpStateKey] = true;
        delete pvResendQueue[sendLpStateKey];
      }

      // 失敗してたらfetchでリトライ
      if (!ok) {
        fetch(ajaxUrl, {
          method: 'POST',
          body: payload,
          keepalive: true
        })
        .then(() => {
          isPvSents[sendLpStateKey] = true;
          delete pvResendQueue[sendLpStateKey];
        })
        .catch(() => {
          pvResendQueue[sendLpStateKey] = true;
          delete isPvSents[sendLpStateKey];
        });
      }
    }

    /**
     * 送信失敗PVの再送用
     */
    function retryPvResendQueue() {
      for (const resendLpStateKey of Object.keys(pvResendQueue)) {
        if (!pvResendQueue[resendLpStateKey]) continue;
        if (isPvSents[resendLpStateKey]) continue;
        sendPvData(resendLpStateKey);
      }
    }

    /**
     * イベントデータ送信関数
     */
    function sendEventData (eventName, option = {}) {
        const now = Date.now();

        retryPvResendQueue();

        if (lpStateKey) flushAttentionCollect(lpStateKey);
        sendAttention(!foreground);
        
        const postData = {};
        const postDataPayload = {};
        let leaveData = {};

        if (option.leaveAttach && !isCtaClickLeaving) {
          leaveData = buildLeaveData();

          if (!hasScrolled) {
            postDataPayload['leaveNoScrollUser'] = true;
          } else if (scrollActionCount === 1) {
            postDataPayload['leaveFirstScrollOnlyUser'] = true;
          }
        }

        if (hasScrolled) {
          postDataPayload['hasScrollUser'] = true;
        }

        if (
          Object.keys(clickData).length === 0 
          && Object.keys(attentionData).length === 0 
          && Object.keys(arrivalData).length === 0
          && Object.keys(leaveData).length === 0
          && !postDataPayload['leaveNoScrollUser']
          && !postDataPayload['leaveFirstScrollOnlyUser']
          && !postDataPayload['hasScrollUser']
        ) return;

        const ajaxUrl = HEATMAP_COLLECT_WS;

        postDataPayload['agentId'] = agentId;
        postDataPayload['adId'] = adId;
        postDataPayload['adNetwork'] = adNetwork;
        postDataPayload['clickData'] = clickData;
        postDataPayload['attentionData'] = attentionData;
        postDataPayload['arrivalData'] = arrivalData;
        postDataPayload['leaveData'] = leaveData;

        postData['uuid'] = uuid;
        postData['domain'] = domain;
        postData['uri'] = currentPathname;
        postData['payload'] = postDataPayload;
        
        const payload = JSON.stringify(postData);

        if(approxBytes <= BEACON_SIZE_MAX) {
          const ok = navigator.sendBeacon(ajaxUrl, payload);

          if(!ok) {
            fetch(ajaxUrl, {method: 'POST', body: payload, keepalive: true}).catch(() => {});
          }
        } else {
          // 32k超えならそれぞれのイベント毎に送信（まず収まる）
          {
            const payload = JSON.stringify({
              ...postData, 
              payload: {
                ...postDataPayload,
                clickData: clickData,
                attentionData: {},
                arrivalData: {},
                leaveData: {}
              }
            });
            const ok = navigator.sendBeacon(ajaxUrl, payload);

            if(!ok) {
              fetch(ajaxUrl, {method: 'POST', body: payload, keepalive: true}).catch(() => {});
            }
          }

          {
            const payload = JSON.stringify({
              ...postData, 
              payload: {
                ...postDataPayload,
                clickData: {},
                attentionData: attentionData, 
                arrivalData: {}, 
                leaveData: {}
              }
            });
            const ok = navigator.sendBeacon(ajaxUrl, payload);

            if(!ok) {
              fetch(ajaxUrl, {method: 'POST', body: payload, keepalive: true}).catch(() => {});
            }
          }

          {
            const payload = JSON.stringify({
              ...postData, 
              payload: {
                ...postDataPayload, 
                clickData: {}, 
                attentionData: {}, 
                arrivalData: arrivalData, 
                leaveData: {}
              }
            });
            const ok = navigator.sendBeacon(ajaxUrl, payload);

            if(!ok) {
              fetch(ajaxUrl, {method: 'POST', body: payload, keepalive: true}).catch(() => {});
            }
          }

          {
            const payload = JSON.stringify({
              ...postData, 
              payload: {
                ...postDataPayload, 
                clickData: {}, 
                attentionData: {}, 
                arrivalData: {}, 
                leaveData: leaveData
              }
            });
            const ok = navigator.sendBeacon(ajaxUrl, payload);

            if(!ok) {
              fetch(ajaxUrl, {method: 'POST', body: payload, keepalive: true}).catch(() => {});
            }
          }
        }        

        //重複送信回避のためクリアする。
        clickData = {};
        attentionData = {};
        arrivalData = {};
        approxBytes = 0;
    }

    /**
     * 
     * 離脱データのもとになる要素データのキャッシュ作成
     */
    function updateLeaveElementPositionCache() {
      leaveElelementPositionCache = {};

      const leaveLpStateKey = lpStateKey || 'default';
      if (!heatmapAttentionElements) return;

      const leaveStep = (h) => {
        if (h <= 120) return 0.5;    // 極小要素は50%刻み
        if (h <= 360) return 0.25;   // 小要素は25%刻み
        if (h <= 1080) return 0.1;   // 中要素は10%刻み
        if (h <= 2160) return 0.05;  // 大要素は5%刻み
        return 0.025;                // 特大要素は2.5%刻み
      };

      heatmapAttentionElements.forEach(el => {
        if (!el.classList.contains(heatmapAttentionClass)) return;
        if (!isAttentionCandidateElement(el)) return;
        if (!isVisibilityElement(el)) return;
        if (isFixed(el)) return;

        const id = el.getAttribute(heatmapDataIdKey);
        if (!id) return;

        const rect = el.getBoundingClientRect();
        const height = Math.max(1, rect.height);
        const absoluteTop = rect.top + window.scrollY;
        const step = leaveStep(height);

        leaveElelementPositionCache[id] = {
          dataId: id,
          stateKey: leaveLpStateKey,
          absoluteTop: rect.top + window.scrollY, 
          absoluteBottom: absoluteTop + height,
          height: height,
          leaveStep: step,
        };
      });
    };

    /**
     * 離脱データ作成
     * ※ FV離脱率として自然に見えるよう、viewport下端ではなく
     *    viewport上端基準で、今見えている先頭要素1件だけに離脱を付与する
     */
    function buildLeaveData(argLpStateKey = lpStateKey) {
      const leaveStateKey = argLpStateKey || 'default';

      const currentScrollY = window.scrollY;
      const currentScrollBottom = currentScrollY + windowHeight;

      const newLeaveData = {};

      const visibleLeaveTargets = Object.values(leaveElelementPositionCache)
        .filter(cached => {
          if (cached.stateKey !== leaveStateKey) return false;

          // viewport外
          if (cached.absoluteBottom <= currentScrollY) return false;
          if (currentScrollBottom <= cached.absoluteTop) return false;

          return true;
        })
        .sort((a, b) => {
          if (a.absoluteTop !== b.absoluteTop) {
            return a.absoluteTop - b.absoluteTop;
          }

          return a.height - b.height;
        });

      if (visibleLeaveTargets.length === 0) {
        return newLeaveData;
      }

      // 今見えている要素のうち、先頭にいる要素だけを離脱対象にする
      const target = visibleLeaveTargets[0];
      const progress = clamp01((currentScrollY - target.absoluteTop) / target.height);

      let ratio = floorCalc(progress, target.leaveStep);

      const ratioKey = ratio.toFixed(4).replace(/\.?0+$/, '');
      const dataKey = `s:${leaveStateKey},id:${target.dataId},r:${ratioKey}`;
      newLeaveData[dataKey] = 1;

      return newLeaveData;
    }

}
